import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { RecipeCard } from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Heart, ArrowLeft } from 'lucide-react';

export const Favorites = () => {
  const { user, userProfile } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!userProfile?.favorites?.length) {
        setRecipes([]);
        setLoading(false);
        return;
      }

      try {
        // Fetch all favorite recipes
        const recipesRef = collection(db, 'recipes');
        const snapshot = await getDocs(recipesRef);
        const allRecipes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filter to only favorites
        const favoriteRecipes = allRecipes.filter(r => 
          userProfile.favorites.includes(r.id)
        );
        
        setRecipes(favoriteRecipes);
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [userProfile?.favorites]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-navy mb-4">
            Bitte melde dich an
          </h2>
          <Link to="/login">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Zur Anmeldung
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Link 
          to="/profil" 
          className="inline-flex items-center gap-2 text-navy hover:text-terracotta mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Zurück zum Profil</span>
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-2">
            Meine Favoriten
          </h1>
          <p className="text-olive">
            {loading ? 'Lade Favoriten...' : `${recipes.length} gespeicherte Rezepte`}
          </p>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-3xl h-80 animate-pulse" />
            ))}
          </div>
        ) : recipes.length > 0 ? (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {recipes.map((recipe, index) => (
              <RecipeCard key={recipe.id} recipe={recipe} index={index} />
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-bone-alt rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-olive" />
            </div>
            <h3 className="font-display text-xl font-semibold text-navy mb-2">
              Keine Favoriten
            </h3>
            <p className="text-olive mb-6">
              Du hast noch keine Rezepte gespeichert.
            </p>
            <Link to="/rezepte">
              <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
                Rezepte entdecken
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
